import { useCallback, useState } from 'react';
import type { FootageAiStyle, FootagePeople } from 'fym-shared/src/music24/musicVideoApi';
import { useVideoCreditGate } from '../../../music24/promote/credits/useVideoCreditGate';
import { aiClipError, type AiClipError } from '../../doc/musicVideoErrors';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { useCreateAiFootageMutation } from '../../musicVideoEditorApi';

export interface AiClipRequest {
  prompt: string;
  style: FootageAiStyle;
  people: FootagePeople;
  /** A referenced new shot (directed video spec §9): at most 3 pictures of the cast library. */
  referenceFileIds?: number[];
  /** The style of the directed video, or the style text of the artist. */
  styleText?: string;
}

/**
 * "Make the clip · 4 credits" (design h1, spec §4.3 AI): the credit check before the click (the
 * owner rule of the Studio: a short balance opens the packs and sends nothing), then
 * `POST /footage/ai` (with the references and the style text of a referenced new shot, at the same
 * price). A 402 opens the packs; a 400 `M24_REFERENCE_INVALID` says that the references are wrong;
 * a 403 shows the lock card (h4); a 409 says that 3 clips are being made. The clip shows in the list at once (the list reads again).
 *
 * Showcase mode has no account: no credit read, and a make opens the sign-up card.
 */
export function useAiClip() {
  const { accountId, catalogItemUuid, data, mode, showUpsell } = useMusicVideoEditor();
  const isShowcase = mode === 'showcase';
  const gate = useVideoCreditGate(isShowcase ? null : accountId);
  const [createAiFootage, { isLoading }] = useCreateAiFootageMutation();
  const [error, setError] = useState<AiClipError | null>(null);

  const make = useCallback(
    async ({ prompt, style, people, referenceFileIds, styleText }: AiClipRequest): Promise<boolean> => {
      setError(null);
      if (isShowcase) {
        showUpsell('aiClips');
        return false;
      }
      if (!gate.canSpend(data.plan.aiClipCredits)) {
        return false;
      }
      try {
        await createAiFootage({
          accountId,
          catalogItemUuid,
          prompt: prompt.trim(),
          style,
          people,
          ...(referenceFileIds === undefined ? {} : { referenceFileIds }),
          ...(styleText === undefined ? {} : { styleText }),
        }).unwrap();
        return true;
      } catch (caught: unknown) {
        const kind = aiClipError(caught);
        console.error('Music video editor: the AI clip did not start', caught);
        if (kind === 'credits') {
          gate.offerCredits();
        }
        setError(kind);
        return false;
      }
    },
    [accountId, catalogItemUuid, createAiFootage, data.plan.aiClipCredits, gate, isShowcase, showUpsell]
  );

  return { make, isBusy: isLoading, error };
}
